import Cookies from "js-cookie"
import { useAxios } from "./useAxios"

export interface RegisterDTO {
  email: string
  password: string
  name: string
  phoneNumber: string
  organizationName: string
  document: string
  organizations: boolean
  source?: string
}

export interface LoginDTO {
  email: string
  password: string
}

export interface ResetPasswordDTO {
  token: string
  password: string
}

export interface ChangePasswordDTO {
  passwordOld: string
  password: string
  passwordConfirm: string
}

export interface NotificationPreferences {
  sale?: boolean
  pendingSale?: boolean
  botDown?: boolean
}

export interface UpdateNotificationSettingsDTO {
  notification?: NotificationPreferences
  notificationSound?: string
}

export interface Session {
  userId: number
  sessionId: number
  email: string
  roles: string[]
  is2AF: boolean
  token: string
  refreshToken: string
  organizationIds: number[]
  tenantId: number
}

const BOTEXPRESS_COOKIE_DOMAIN = '.mybotexpress.com'

export const setAuthCookie = (name: string, value: string) => {
  const sameSiteFlags = process.env.VUE_APP_ENVIRONMENT === 'development' ? '' : 'SameSite=None; Secure;'
  document.cookie = `${name}=${value}; path=/; domain=${process.env.VUE_APP_DASHBOARD_URL_COOKIE}; max-age=432000; ${sameSiteFlags}`
  document.cookie = `${name}=${value}; path=/; domain=${BOTEXPRESS_COOKIE_DOMAIN}; max-age=432000; ${sameSiteFlags}`
}

export const removeAuthCookie = (name: string) => {
  Cookies.remove(name, { path: '/', domain: process.env.VUE_APP_DASHBOARD_URL_COOKIE })
  Cookies.remove(name, { path: '/', domain: BOTEXPRESS_COOKIE_DOMAIN })
}

export const useAuth = () => {
  const authUser: { userId: number } | null = Cookies.get('authUser')
    ? JSON.parse(Cookies.get('authUser') as string)
    : null

  const axios = useAxios(process.env.VUE_APP_API_REST_BASE_URL)
  // Notificações push vivem na API de bots (API_Bot_Express), não na API de contas/auth.
  const botsApiAxios = useAxios(process.env.VUE_APP_API_BASE_URL)

  const updateTokensOnLogin = (payload: Session) => {
    if (payload?.token) {
      setAuthCookie('token', payload.token)
    }

    if (payload?.roles) {
      setAuthCookie('roles', JSON.stringify(payload.roles))
    }

    if (payload?.is2AF !== undefined) {
      setAuthCookie('is2AF', String(payload.is2AF))
    }

    setAuthCookie('authUser', JSON.stringify(payload))
  }

  async function register(payload: RegisterDTO) {
    const res = await axios.post('/accounts', payload)
    updateTokensOnLogin(res.data)
    return res.data
  }

  async function login(payload: LoginDTO) {
    const res = await axios.post('/sessions', payload)

    updateTokensOnLogin(res.data)

    return res.data
  }

  function logout() {
    removeAuthCookie('token')
    removeAuthCookie('authUser')
    removeAuthCookie('is2AF')
    removeAuthCookie('valid2AF')
    removeAuthCookie('roles')
    removeAuthCookie('organization')
  }

  async function sendResetPasswordLink(email: string) {
    const res = await axios.put('/accounts/password/recovery', { email, origin: 'bot-express' })
    return res.data
  }

  async function resetPassword(payload: ResetPasswordDTO) {
    const res = await axios.patch('/accounts/password/recovery', payload)
    return res.data
  }

  async function changePassword(payload: ChangePasswordDTO) {
    const res = await axios.patch('/accounts', payload)
    return res.data
  }

  async function getProfile() {
    const res = await botsApiAxios.get('/accounts')
    return res.data
  }

  async function updateNotificationSettings(payload: UpdateNotificationSettingsDTO) {
    const res = await botsApiAxios.put('/accounts', payload)
    return res.data
  }

  function getFingerprint(): string {
    const stored = localStorage.getItem('deviceFingerprint')
    if (stored) return stored

    const fingerprint = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? (crypto as unknown as { randomUUID(): string }).randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`

    localStorage.setItem('deviceFingerprint', fingerprint)
    return fingerprint
  }

  function getDeviceInfo(): { os: string, deviceModel: string } {
    const ua = navigator.userAgent

    const os = /Windows/.test(ua) ? 'Windows'
      : /Mac OS/.test(ua) ? 'macOS'
      : /Android/.test(ua) ? 'Android'
      : /iPhone|iPad|iPod/.test(ua) ? 'iOS'
      : /Linux/.test(ua) ? 'Linux'
      : 'Desconhecido'

    const deviceModel = /Chrome/.test(ua) ? 'Chrome'
      : /Firefox/.test(ua) ? 'Firefox'
      : /Safari/.test(ua) ? 'Safari'
      : /Edg/.test(ua) ? 'Edge'
      : 'Navegador'

    return { os, deviceModel }
  }

  async function upsertPushNotificationToken(token: string) {
    const { os, deviceModel } = getDeviceInfo()

    const res = await botsApiAxios.put('/notifications/push-token', {
      token,
      fingerprint: getFingerprint(),
      platform: 'web',
      os,
      deviceModel
    })
    return res.data
  }

  async function validate2FA(code: string) {
    const token = Cookies.get('token')
    const res = await axios.post('/accounts/2af/validate', { code }, {
      headers: { Authorization: `Bearer ${token}` }
    })

    if (res.data.is2AF && res.data.token) {
      removeAuthCookie('is2AF')
      setAuthCookie('valid2AF', 'true')
    }

    res.data.is2AF = false

    updateTokensOnLogin(res.data)

    return res.data
  }

  return {
    authUser,
    register,
    login,
    logout,
    sendResetPasswordLink,
    resetPassword,
    changePassword,
    validate2FA,
    getProfile,
    updateNotificationSettings,
    upsertPushNotificationToken,
    getFingerprint,
  }
}
